import { useCallback, useMemo, useState } from 'react';
import cl from 'classnames';
import filter from 'lodash/filter';
import size from 'lodash/size';
import take from 'lodash/take';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { CacheFetchProductsAggregationsQueryResponseAggregations } from '../../../../../../products/queries/fetchCacheProductsAggregations.query';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { ThreeDStockProductsFiltersBrandsFilterItem } from './components/ThreeDStockProductsFiltersBrandsFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { categoriesKeys, words } from '../../../../../../../locales/keys';

const INITIAL_TAGS_COUNT = 6;
const fieldName = FetchProductsFilterFields.BRAND_ID;

interface ThreeDStockProductsFiltersBrandsFilterProps {
  productsAggregations: CacheFetchProductsAggregationsQueryResponseAggregations;
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
  isCompact?: boolean;
}

function ThreeDStockProductsFiltersBrandsFilter({
  productsAggregations,
  changeProductsFilters,
  productsFilters,
  isCompact
}: ThreeDStockProductsFiltersBrandsFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [minimized, setMinimized] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const toggleMinimize = useCallback<() => void>(() => {
    setMinimized((prevState) => !prevState);
  }, [setMinimized]);

  const brands = productsAggregations?.brands;

  const checkedBrands: string[] = useMemo(
    () => productsFilters[fieldName]?.in || [],
    [productsFilters]
  );

  const handleCheckedBrand = useCallback(
    (brandId: string, checked: boolean) => {
      const newBrands = checked
        ? [...checkedBrands, brandId]
        : filter(checkedBrands, (brand) => brand !== brandId);

      changeProductsFilters(
        {
          [fieldName]: {
            in: newBrands
          }
        },
        size(newBrands) > 0 ? [] : [fieldName]
      );
    },
    [checkedBrands, changeProductsFilters]
  );

  if (size(brands) === 0) {
    return null;
  }

  return (
    <div className={cl(isCompact ? 'p-2' : 'p-4')}>
      <div className={cl('flex flex-col', isCompact ? 'gap-1.5' : 'gap-3')}>
        <ButtonHelper
          className="flex items-center gap-2 rounded-sm"
          dataGa="brands_filter"
          onClick={toggleIsOpen}
        >
          <span
            className={cl(
              'font-semibold text-left flex-1',
              isCompact ? 'text-xs' : 'text-sm'
            )}
          >
            <Translate id={categoriesKeys.brands.singular} />
          </span>

          <Icon
            icon={isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP}
            className={cl('text-gray-500', isCompact ? 'h-4 w-4' : 'h-5 w-5')}
          />
        </ButtonHelper>

        {isOpen && (
          <ul>
            {(minimized ? take(brands, INITIAL_TAGS_COUNT) : brands)?.map(
              (brand, index) => (
                <ThreeDStockProductsFiltersBrandsFilterItem
                  key={index}
                  checked={checkedBrands?.includes(brand.id)}
                  onCheckedBrand={handleCheckedBrand}
                  brand={brand}
                  isCompact={isCompact}
                />
              )
            )}

            {size(brands) > INITIAL_TAGS_COUNT && (
              <PureIconButtonHelper
                className="p-0 space-x-1 flex gap-1 text-gray-900 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 leading-5 focus:ring-base text-current hover:underline transition-colors duration-150 ease-out"
                i18nText={minimized ? words.showAll : words.showLess}
                i18nTextClassName={cl(
                  isCompact ? 'text-xs' : 'text-sm',
                  'underline'
                )}
                onClick={toggleMinimize}
                icon={
                  minimized
                    ? IconsEnum.CHEVRON_DOWN_SOLID
                    : IconsEnum.CHEVRON_UP
                }
                iconClassName={isCompact ? 'w-4 h-4' : 'w-5 h-5'}
              />
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersBrandsFilter;
